import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
} from 'chart.js'
import moment from 'moment'

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
)

// Charts for the Post Event Report, drawn off-screen with Chart.js and
// embedded as PNGs (same approach as the KPI PDFs). Drawn at 2x the point
// size they occupy so they print crisply. Every chart is a single series in
// the house teal; identity never rests on colour.

const TEAL = '#239AA4'
const TEAL_FILL = 'rgba(35, 154, 164, 0.12)'
const AXIS = '#64748B'
const GRID = '#E2E8F0'
const SCALE = 2
const FULL_WIDTH = 535

const font = { size: 18, family: 'Helvetica, Arial, sans-serif' }

const renderToImage = (type, data, options, width, height) => {
  const canvas = document.createElement('canvas')
  canvas.width = width * SCALE
  canvas.height = height * SCALE
  const chart = new ChartJS(canvas.getContext('2d'), {
    type,
    data,
    options: {
      ...options,
      responsive: false,
      animation: false,
      devicePixelRatio: 1,
      layout: { padding: { right: 12, top: 6 } },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
    },
  })
  const src = canvas.toDataURL('image/png')
  chart.destroy()
  return { src, width, height }
}

const axis = (extra = {}) => ({
  ticks: { color: AXIS, font, ...extra.ticks },
  grid: { color: GRID, drawTicks: false, ...extra.grid },
  border: { display: false },
  ...extra.rest,
})

// Hours re-ordered to run from 05:00 (the operational day start)
const HOUR_ORDER = Array.from({ length: 24 }, (_, i) => (i + 5) % 24)

export const hourlyChart = (hourly, width = FULL_WIDTH, height = 120) => {
  if (!hourly?.some((v) => v > 0)) return null
  return renderToImage(
    'bar',
    {
      labels: HOUR_ORDER.map((h) => String(h).padStart(2, '0')),
      datasets: [
        {
          data: HOUR_ORDER.map((h) => hourly[h]),
          backgroundColor: TEAL,
          borderRadius: 4,
          maxBarThickness: 28,
        },
      ],
    },
    {
      scales: {
        x: axis({ grid: { display: false } }),
        y: axis({ ticks: { precision: 0, maxTicksLimit: 5 }, rest: { beginAtZero: true } }),
      },
    },
    width,
    height,
  )
}

export const occupancyChart = (series, width = FULL_WIDTH, height = 110) => {
  if (!series?.length || !series.some((p) => p.count > 0)) return null
  const multiDay =
    moment(series[0].t).format('YYYY-MM-DD') !==
    moment(series[series.length - 1].t).format('YYYY-MM-DD')
  return renderToImage(
    'line',
    {
      labels: series.map((p) => moment(p.t).format(multiDay ? 'ddd HH:mm' : 'HH:mm')),
      datasets: [
        {
          data: series.map((p) => p.count),
          borderColor: TEAL,
          borderWidth: 2.5,
          backgroundColor: TEAL_FILL,
          fill: true,
          pointRadius: 0,
          stepped: true,
        },
      ],
    },
    {
      scales: {
        x: axis({ grid: { display: false }, ticks: { maxTicksLimit: 10, maxRotation: 0 } }),
        y: axis({ ticks: { precision: 0, maxTicksLimit: 5 }, rest: { beginAtZero: true } }),
      },
    },
    width,
    height,
  )
}

// Daily totals for the day-by-day comparison
export const dailyChart = (days, getValue, width = FULL_WIDTH, height = 110) => {
  if (!days?.length || !days.some((d) => getValue(d) > 0)) return null
  return renderToImage(
    'bar',
    {
      labels: days.map((d) => moment(d.key, 'YYYY-MM-DD').format('ddd D MMM')),
      datasets: [
        {
          data: days.map(getValue),
          backgroundColor: TEAL,
          borderRadius: 4,
          maxBarThickness: 40,
        },
      ],
    },
    {
      scales: {
        x: axis({ grid: { display: false } }),
        y: axis({ ticks: { precision: 0, maxTicksLimit: 5 }, rest: { beginAtZero: true } }),
      },
    },
    width,
    height,
  )
}

// Every image the report needs, keyed for the PDF sections
export const renderReportCharts = (data, { byDay }) => {
  const clinical = data.overall.clinical
  const charts = {
    hourly: hourlyChart(clinical.hourly),
    dailyPresentations: dailyChart(data.days, (d) => d.clinical.total),
    cadHourly: data.controlRoom ? hourlyChart(data.controlRoom.summary.hourly, FULL_WIDTH, 95) : null,
    occupancy: {},
    days: {},
  }
  ;(data.treatment?.locations || []).forEach((loc) => {
    charts.occupancy[loc.id] = occupancyChart(loc.series, FULL_WIDTH, 90)
  })
  if (byDay) {
    data.days.forEach((day) => {
      charts.days[day.key] = {
        hourly: hourlyChart(day.clinical.hourly, FULL_WIDTH, 95),
      }
    })
  }
  return charts
}
